import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, MapPin, CheckCircle, Phone, Mail, Calendar, Award, Shield, Image as ImageIcon, Send, Loader2, Clock, DollarSign } from 'lucide-react';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import QuoteModal from '../components/QuoteModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProviderProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [hasAcceptedQuote, setHasAcceptedQuote] = useState(false);
  
  const { user } = useAuth();
  
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    service: '',
    notes: ''
  });
  
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    message: ''
  });
  
  // Mock portfolio images (in production, these would come from backend)
  const portfolioImages = [
    'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581578968194-2d57d0f08659?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581578968116-7dc79c45c053?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581578968272-c9a2e656a096?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop'
  ];
  
  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [providerRes, reviewsRes, packagesRes] = await Promise.all([
        axios.get(`${API_URL}/api/providers/${id}`),
        axios.get(`${API_URL}/api/reviews?providerId=${id}`),
        axios.get(`${API_URL}/api/packages?providerId=${id}`)
      ]);
      setProvider(providerRes.data);
      setReviews(reviewsRes.data);
      setPackages(packagesRes.data);
      
      // Check if logged-in user has accepted quote with this provider
      if (user?.email) {
        try {
          const quotesRes = await axios.get(`${API_URL}/api/quotes?email=${user.email}`);
          const acceptedQuote = quotesRes.data.some(
            quote => quote.providerId === id && quote.status === 'accepted'
          );
          setHasAcceptedQuote(acceptedQuote);
        } catch (error) {
          console.error('Error checking quotes:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching provider data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load provider information.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setBookingLoading(true);
    
    // Simulate booking API call
    setTimeout(() => {
      toast({
        title: 'Booking Request Sent',
        description: 'The provider will contact you shortly to confirm your appointment.'
      });
      setBookingModalOpen(false);
      setBookingData({ date: '', time: '', service: '', notes: '' });
      setBookingLoading(false);
    }, 1500);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    
    // Simulate contact API call
    setTimeout(() => {
      toast({
        title: 'Message Sent',
        description: 'Your message has been sent to the provider.'
      });
      setContactModalOpen(false);
      setContactData({ name: '', email: '', message: '' });
      setContactLoading(false);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
          <Button onClick={() => navigate('/providers')} className="mt-4">Back to Providers</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : provider.rating || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Provider Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <img
                  src={provider.image || 'https://via.placeholder.com/150'}
                  alt={provider.name}
                  className="w-32 h-32 rounded-lg object-cover"
                />
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{provider.name}</h1>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                        <span className="font-semibold">{averageRating}</span>
                        <span className="ml-1">({reviews.length} reviews)</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {provider.location}
                      </div>
                    </div>
                    {provider.verified && (
                      <div className="flex items-center text-green-600 mb-3">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Verified Provider</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <p className="text-gray-700 mb-4">{provider.description}</p>
                
                <div className="flex flex-wrap gap-2">
                  {provider.services?.map((service, idx) => (
                    <Badge key={idx} variant="secondary">{service}</Badge>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t">
              <Button
                onClick={() => setQuoteModalOpen(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                Request Quote
              </Button>
              <Button
                onClick={() => setBookingModalOpen(true)}
                variant="outline"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Book Appointment
              </Button>
              <Button
                onClick={() => setContactModalOpen(true)}
                variant="outline"
              >
                <Mail className="h-4 w-4 mr-2" />
                Contact
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs Section */}
        <Tabs defaultValue="about" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="packages">Service Packages</TabsTrigger>
            <TabsTrigger value="booking">Book Appointment</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>

          {/* About Tab */}
          <TabsContent value="about" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>About {provider.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <Award className="h-5 w-5 text-orange-600 mr-3 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Years of Experience</p>
                      <p className="text-gray-600">{provider.experience || '5+'} years</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-orange-600 mr-3 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Licensed & Insured</p>
                      <p className="text-gray-600">Fully certified professional</p>
                    </div>
                  </div>
                  {provider.businessHours && (
                    <div className="flex items-start">
                      <Clock className="h-5 w-5 text-orange-600 mr-3 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Business Hours</p>
                        <p className="text-gray-600">{provider.businessHours}</p>
                      </div>
                    </div>
                  )}
                  
                  {hasAcceptedQuote && (
                    <div className="flex items-start">
                      <Mail className="h-5 w-5 text-orange-600 mr-3 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Email</p>
                        <p className="text-gray-600">{provider.email}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-900 mb-2">Services Offered</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {provider.services?.map((service, idx) => (
                      <li key={idx}>{service}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ImageIcon className="h-5 w-5 mr-2 text-orange-600" />
                  Project Portfolio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {portfolioImages.map((img, idx) => (
                    <div
                      key={idx}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => {
                        setSelectedImage(img);
                        setGalleryModalOpen(true);
                      }}
                    >
                      <img
                        src={img}
                        alt={`Portfolio ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Service Packages Tab */}
          <TabsContent value="packages">
            {packages.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No packages available</h3>
                  <p className="text-gray-600">This provider hasn't created any service packages yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {packages.map((pkg, idx) => (
                  <Card key={pkg.id}>
                    <CardHeader>
                      <CardTitle>{pkg.name}</CardTitle>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-3xl font-bold text-gray-900">${pkg.price}</span>
                        <span className="text-sm text-gray-500">CAD</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">+ 10% platform fee at checkout</p>
                      <p className="text-sm text-gray-600 flex items-center mt-2">
                        <Clock className="h-4 w-4 mr-1" />
                        {pkg.duration}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>
                      <ul className="space-y-2 mb-4">
                        {pkg.features.map((feature, fidx) => (
                          <li key={fidx} className="flex items-start text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        onClick={() => setQuoteModalOpen(true)}
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        Select Package
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Book Appointment Tab */}
          <TabsContent value="booking">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book an Appointment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {provider.businessHours && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-900">Business Hours</p>
                          <p className="text-blue-700 mt-1">{provider.businessHours}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-orange-900">Response Time</p>
                        <p className="text-orange-700 mt-1">{provider.responseTime}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900">How to Book:</h3>
                    <ol className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          1
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Select a Service Package</p>
                          <p className="text-sm text-gray-600">Choose from available packages in the "Service Packages" tab</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          2
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Request a Quote</p>
                          <p className="text-sm text-gray-600">Click "Get Quote" and provide your project details</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          3
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Get Confirmation</p>
                          <p className="text-sm text-gray-600">The provider will review and accept your request</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          4
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Complete Payment</p>
                          <p className="text-sm text-gray-600">Pay securely through our platform and schedule your service</p>
                        </div>
                      </li>
                    </ol>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={() => setQuoteModalOpen(true)}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      size="lg"
                    >
                      Request Quote & Book Appointment
                    </Button>
                  </div>

                  {provider.serviceArea && (
                    <div className="bg-gray-50 rounded-lg p-4 mt-4">
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-gray-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Service Area</p>
                          <p className="text-gray-700 mt-1">{provider.serviceArea}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle>Customer Reviews ({reviews.length})</CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${star <= Math.round(averageRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <span className="font-semibold">{averageRating}</span>
                  <span className="text-gray-600">out of 5</span>
                </div>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No reviews yet</p>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b last:border-b-0 pb-4 last:pb-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-gray-900">{review.customerName}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm text-gray-500">
                                {new Date(review.date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-700">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Modal */}
      <Dialog open={bookingModalOpen} onOpenChange={setBookingModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book an Appointment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBooking} className="space-y-4">
            <div>
              <Label>Preferred Date</Label>
              <Input
                type="date"
                value={bookingData.date}
                onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Preferred Time</Label>
              <Input
                type="time"
                value={bookingData.time}
                onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Service Type</Label>
              <select
                value={bookingData.service}
                onChange={(e) => setBookingData({ ...bookingData, service: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select a service</option>
                {provider.services?.map((service, idx) => (
                  <option key={idx} value={service}>{service}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Additional Notes</Label>
              <Textarea
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                rows={3}
                placeholder="Any specific requirements or details..."
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setBookingModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={bookingLoading} className="flex-1 bg-orange-600 hover:bg-orange-700">
                {bookingLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Booking...</>) : 'Confirm Booking'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contact {provider.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div>
              <Label>Your Name</Label>
              <Input
                value={contactData.name}
                onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={contactData.email}
                onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={contactData.message}
                onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                rows={4}
                placeholder="Your message..."
                required
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setContactModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={contactLoading} className="flex-1 bg-orange-600 hover:bg-orange-700">
                {contactLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : (<><Send className="mr-2 h-4 w-4" />Send Message</>)}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gallery Modal */}
      <Dialog open={galleryModalOpen} onOpenChange={setGalleryModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Portfolio Image</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <img src={selectedImage} alt="Portfolio" className="w-full rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Modal */}
      <QuoteModal
        isOpen={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
        provider={provider}
      />

      <Footer />
      <Toaster />
    </div>
  );
};

export default ProviderProfilePage;